
import React, { useState, useRef, useEffect } from 'react';
import './PostValidator.css';

function PostValidator() {
  const [username, setUsername] = useState('');
  const [subreddit, setSubreddit] = useState('');
  const [postTitle, setPostTitle] = useState('');
  const [postContent, setPostContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const textareaRef = useRef(null);

  const handleFocus = (event) => event.target.select();

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      // Use a small delay to ensure content is rendered
      const timer = setTimeout(() => {
        // Reset height to auto to get the scrollHeight
        textareaRef.current.style.height = 'auto';
        // Set height to scrollHeight (plus a little padding)
        const newHeight = Math.min(textareaRef.current.scrollHeight + 2, 500);
        textareaRef.current.style.height = `${newHeight}px`;
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [postContent]);

  // Clean up AutoMod config details when component unmounts or validation result changes
  useEffect(() => {
    return () => {
      // Close any open details elements when component unmounts
      const detailsElements = document.querySelectorAll('details');
      detailsElements.forEach(details => {
        details.removeAttribute('open');
      });
    };
  }, []);

  const getUsername = (input) => {
    const trimmedInput = input.trim();
    // Match /user/username or /u/username from a URL
    const urlMatch = trimmedInput.match(/reddit\.com\/(?:user|u)\/([^/]+)/);
    if (urlMatch) {
      return urlMatch[1];
    }
    // Match u/username format
    if (trimmedInput.startsWith('u/')) {
      return trimmedInput.substring(2);
    }
    // Assume it's just the username
    return trimmedInput;
  };

  const getSubredditName = (input) => {
    const trimmedInput = input.trim();
    const match = trimmedInput.match(/reddit\.com\/r\/([^/]+)/);
    if (match) {
      return match[1];
    }
    if (trimmedInput.startsWith('r/')) {
      return trimmedInput.substring(2);
    }
    return trimmedInput;
  };

  const handleValidate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setValidationResult(null);

    const sanitizedUsername = getUsername(username);
    const sanitizedSubreddit = getSubredditName(subreddit);

    try {
      const response = await fetch('/api/validate_post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          username: sanitizedUsername, 
          subreddit: sanitizedSubreddit,
          post_title: postTitle,
          post_content: postContent 
        })
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Network response was not ok');
      }
      
      const data = await response.json();
      console.log('Received data from backend:', data);
      // Log the structure of the received data to help with debugging
      console.log('Data structure:', Object.keys(data));
      if (data.post_violations) {
        console.log('Post violations structure:', Object.keys(data.post_violations));
        console.log('Violations array:', data.post_violations.violations);
      }
      // Log AutoMod config if available
      if (data.automod_config) {
        console.log('AutoMod config:', data.automod_config);
      }
      setValidationResult(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };



  // Function to extract karma and age restrictions from AutoModerator config
  const extractAutomodRestrictions = (configText) => {
    if (!configText) return null;
    
    const restrictions = {};
    
    // Look for karma restrictions
    const karmaMatch = configText.match(/(karma|score):\s*(\d+)/i);
    if (karmaMatch) {
      restrictions.karma = parseInt(karmaMatch[2]);
    }
    
    // Look for age restrictions (in days)
    const ageMatch = configText.match(/(age|account_age):\s*(\d+)/i);
    if (ageMatch) {
      restrictions.age = parseInt(ageMatch[2]);
    }
    
    // Look for approval restrictions
    const approvalMatch = configText.match(/(approval|approve):\s*(true|false)/i);
    if (approvalMatch) {
      restrictions.requiresApproval = approvalMatch[2].toLowerCase() === 'true';
    }
    
    return Object.keys(restrictions).length > 0 ? restrictions : null;
  };

  return (
    <>
      <title>Post Validator - WizeHelper</title>
      <meta name="description" content="Check if you're allowed to post in a subreddit and if your post violates any of its rules." />
      <div className="PostValidator">
      <div className="page-container">

        {!loading && !error && !validationResult && !username && !subreddit && !postTitle && !postContent && (
            <div className="welcome-section">
                <div className="welcome-content">
                    <h2 className="welcome-title">✅ Validate Your Reddit Post Before You Submit</h2>
                    <p className="welcome-description">
                        Check if you're allowed to post in a subreddit and if your post violates any of its rules. Avoid having your posts removed by validating them first.
                    </p>
                    <div className="welcome-steps">
                        <div className="step-item">
                            <span className="step-number">1</span>
                            <div className="step-content">
                                <h4>Enter User and Subreddit</h4>
                                <p>Provide your Reddit username and the target subreddit.</p>
                            </div>
                        </div>
                        <div className="step-item">
                            <span className="step-number">2</span>
                            <div className="step-content">
                                <h4>Enter Post Details</h4>
                                <p>Enter the title and content of your post.</p>
                            </div>
                        </div>
                        <div className="step-item">
                            <span className="step-number">3</span>
                            <div className="step-content">
                                <h4>Validate</h4>
                                <p>Click "Validate" to check for potential issues.</p>
                            </div>
                        </div>
                        <div className="step-item">
                            <span className="step-number">4</span>
                            <div className="step-content">
                                <h4>Review Results</h4>
                                <p>Get a clear "Yes" or "No" on whether you can post, along with a list of any rule violations.</p>
                            </div>
                        </div>
                    </div>
                    <div className="welcome-cta">
                        <p><strong>Ready to get started?</strong> Enter the details in the form below!</p>
                    </div>
                </div>
            </div>
        )}

        <div className="search-container">
          <form onSubmit={handleValidate}>
            <input
              type="text"
              value={subreddit}
              onFocus={handleFocus}
              onChange={(e) => setSubreddit(e.target.value)}
              placeholder="Enter Subreddit or URL"
              required
              className="search-input"
              aria-label="Subreddit or URL"
            />
            <input 
              type="text" 
              value={username} 
              onFocus={handleFocus} 
              onChange={(e) => setUsername(e.target.value)} 
              placeholder="Enter Username or Profile URL" 
              required 
              className="search-input"
              aria-label="Username or Profile URL"
            />
            <input
              type="text"
              value={postTitle}
              onFocus={handleFocus}
              onChange={(e) => setPostTitle(e.target.value)}
              placeholder="Enter post title"
              required
              className="search-input"
              aria-label="Post title"
            />
            <textarea
              ref={textareaRef}
              value={postContent}
              onFocus={handleFocus}
              onChange={(e) => setPostContent(e.target.value)}
              placeholder="Enter your post content"
              required
              className="search-input auto-resize-textarea"
              aria-label="Post content"
            />
            <div className="form-row">
              <button type="submit" disabled={loading} className="button-primary" aria-label="Validate Post">
                {loading ? <div className="spinner"></div> : 'Validate'}
              </button>
            </div>
          </form>
        </div>

        {error && <p className="error">Error: {error}</p>}

        {loading && <p>Validating post...</p>}

        {validationResult && (
          <div className="results-container">
            {console.log("Rendering results container")}
            <h3>Validation Results</h3>
            <div className="result-item">
              <strong>User Allowed to Post:</strong> 
              <span className={validationResult.user_allowed ? 'success' : 'error'}>
                {validationResult.user_allowed ? 'Yes' : 'No'}
              </span>
            </div>
            {console.log("User allowed:", validationResult.user_allowed)}

            {validationResult.user_info && (
              <div className="result-item">
                <h4>User Information</h4>
                <p>Username: {validationResult.user_info.username}</p>
                <p>Link Karma: {validationResult.user_info.link_karma}</p>
                <p>Comment Karma: {validationResult.user_info.comment_karma}</p>
                <p>Account Created: {new Date(validationResult.user_info.created_utc * 1000).toLocaleDateString()}</p>
              </div>
            )}
            {console.log("User info:", validationResult.user_info)}

            <div className="result-item">
              <strong>Post Violations:</strong>
              {validationResult.post_violations ? (
                validationResult.post_violations.violations && validationResult.post_violations.violations.length > 0 ? (
                  <ul className="violations-list">
                    {validationResult.post_violations.violations.map((violation, index) => (
                      <li key={index} className="violation-item">{violation}</li>
                    ))}
                  </ul>
                ) : (
                  <span className="success">No violations found.</span>
                )
              ) : (
                <span className="error">No violations data available.</span>
              )}
            </div>
            {console.log("Post violations:", validationResult.post_violations)}

            {validationResult.subreddit_rules && validationResult.subreddit_rules.length > 0 && (
              <div className="result-item">
                <h4>Subreddit Rules</h4>
                <ul className="rules-list">
                  {validationResult.subreddit_rules.map((rule, index) => (
                    <li key={index} className="rule-item">
                      <strong>{rule.short_name}</strong>: {rule.description}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {validationResult.automod_config && validationResult.automod_config.config && (
              <div className="result-item">
                <h4>AutoModerator Restrictions</h4>
                {(() => {
                  const restrictions = extractAutomodRestrictions(validationResult.automod_config.config);
                  if (restrictions) {
                    return (
                      <div>
                        {restrictions.karma && (
                          <p><strong>Minimum Karma Required:</strong> {restrictions.karma}</p>
                        )}
                        {restrictions.age && (
                          <p><strong>Minimum Account Age:</strong> {restrictions.age} days</p>
                        )}
                        {restrictions.requiresApproval !== undefined && (
                          <p><strong>Post Requires Approval:</strong> {restrictions.requiresApproval ? 'Yes' : 'No'}</p>
                        )}
                      </div>
                    );
                  } else {
                    return <p>No karma or age restrictions found in AutoModerator config.</p>;
                  }
                })()}
                <details>
                  <summary>View Full AutoModerator Config</summary>
                  <pre className="automod-config">{validationResult.automod_config.config}</pre>
                </details>
              </div>
            )}
            {console.log("Subreddit rules:", validationResult.subreddit_rules)}
          </div>
        )}
      </div>
    </div>
    </>
  );
}

export default PostValidator;
